<template>
  <div class="pe-option-container">
    <div v-if="availableOptions.filter(o => o.show_title).map(o => o.title).length > 0">
      <p :class="[globalConfig.classes?.label]">
        {{availableOptions.filter(o => o.show_title).map(o => o.title).join(', ')}}
      </p>
    </div>

    <!-- Si quedan opciones por firmar y no están cargando -->
    <div v-if="availableOptions.length > 0">
      <!-- Usamos la primera opción disponible por defecto -->
      <div v-for="opt in availableOptions.slice(0, 1)" :key="opt.id" class="pe-signature-wrapper">
        <canvas ref="signatureCanvas" class="pe-signature-canvas" @mousedown="startDrawing" @mousemove="draw"
          @mouseup="stopDrawing" @mouseout="stopDrawing" @touchstart.prevent="startDrawingTouch"
          @touchmove.prevent="drawTouch" @touchend.prevent="stopDrawing"></canvas>

        <div v-if="opt.isLoading" class="pe-signature-loading">
          <span class="spinner"></span>
          <span class="uploading-text">Guardando firma...</span>
        </div>

        <div class="pe-signature-actions" v-if="!opt.isLoading">
          <PeButton variant="secondary" @click.prevent="clearCanvas">Borrar</PeButton>
          <PeButton variant="primary" @click.prevent="saveSignature(opt)" :disabled="isEmpty">Guardar Firma</PeButton>
        </div>
      </div>
    </div>

    <div v-if="localErrors.length > 0" class="pe-error-container">
      <div v-for="(err, index) in localErrors" :key="index" class="pe-toast-error"
        :class="[err.type, globalConfig.classes?.input]">
        {{ err.message }}
      </div>
    </div>

    <!-- Firmas subidas -->
    <div v-if="filledOptions.length > 0" class="pe-ListOptions">
      <div v-for="opt in filledOptions" :key="opt.id" class="pe-ListItem">
        <div class="pe-file-info" :class="[globalConfig.classes?.input]">
          <template v-if="opt.isLoading">
            <span class="spinner"></span>
            <span class="uploading-text">Procesando...</span>
          </template>
          <template v-else>
            <img :src="opt.value" alt="Firma digital" class="pe-signature-preview" />
          </template>
        </div>
        <button v-if="!opt.isLoading" class="pe-delete-btn" @click="removeSignature(opt.id)" title="Eliminar firma">
          ✕
        </button>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted, nextTick, shallowRef, watch } from 'vue';
import PeButton from '../Shared/PeButton.vue';

const props = defineProps({
  question: { type: Object, required: true },
});

const engine = inject('formEngine');
const globalConfig = inject('engineConfig');

// DOM Refs
const signatureCanvas = ref(null);
const ctx = shallowRef(null);
const localErrors = ref([]);

// State
const isDrawing = ref(false);
const isEmpty = ref(true);

// Computed Option Groups
const signatureOptions = computed(() => props.question.optionGroups['signature'] || []);
const availableOptions = computed(() => signatureOptions.value.filter(o => !o.value && !o.isLoading));
const filledOptions = computed(() => signatureOptions.value.filter(o => o.value || o.isLoading));

const showToast = (msg, type = 'error') => {
  localErrors.value.push({ message: msg, type });
  setTimeout(() => localErrors.value.shift(), 3000);
};

// --- CANVAS LOGIC ---
const initCanvas = () => {
  const canvasArray = signatureCanvas.value;
  const canvas = Array.isArray(canvasArray) ? canvasArray[0] : canvasArray;
  if (!canvas) return;

  const rect = canvas.getBoundingClientRect();
  // Evitar contexto nulo si el canvas no se dibujó todavía debido a modales
  if (rect.width === 0) {
    setTimeout(initCanvas, 150);
    return;
  }

  const dpr = window.devicePixelRatio || 1;
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;

  const context = canvas.getContext('2d');
  context.scale(dpr, dpr);
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.lineWidth = 3;
  context.strokeStyle = '#000000'; // Trazo negro

  ctx.value = context;
};

onMounted(async () => {
  if (availableOptions.value.length > 0) {
    await nextTick();
    initCanvas();
  }
});

watch(availableOptions, async (newVal, oldVal) => {
  if (newVal.length > 0 && (!oldVal || oldVal.length === 0)) {
    await nextTick();
    initCanvas();
    isEmpty.value = true;
  }
});

const getCoordinates = (event) => {
  const canvasArray = signatureCanvas.value;
  const canvas = Array.isArray(canvasArray) ? canvasArray[0] : canvasArray;
  if (!canvas) return { x: 0, y: 0 };

  const rect = canvas.getBoundingClientRect();
  if (event.touches && event.touches.length > 0) {
    return {
      x: event.touches[0].clientX - rect.left,
      y: event.touches[0].clientY - rect.top,
    };
  }
  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top,
  };
};

const startDrawing = (e) => {
  if (!ctx.value) return;
  isDrawing.value = true;
  isEmpty.value = false;
  const { x, y } = getCoordinates(e);
  ctx.value.beginPath();
  ctx.value.moveTo(x, y);
};

const startDrawingTouch = (e) => startDrawing(e);

const draw = (e) => {
  if (!isDrawing.value || !ctx.value) return;
  const { x, y } = getCoordinates(e);
  ctx.value.lineTo(x, y);
  ctx.value.stroke();
};

const drawTouch = (e) => draw(e);

const stopDrawing = () => {
  if (!isDrawing.value || !ctx.value) return;
  isDrawing.value = false;
  ctx.value.closePath();
};

const clearCanvas = () => {
  const canvasArray = signatureCanvas.value;
  const canvas = Array.isArray(canvasArray) ? canvasArray[0] : canvasArray;
  if (!canvas || !ctx.value) return;

  const dpr = window.devicePixelRatio || 1;
  ctx.value.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
  ctx.value.beginPath();

  isEmpty.value = true;
};

// --- GUADRADO Y COMPARTICIÓN ---
const saveSignature = async (opt) => {
  const canvasArray = signatureCanvas.value;
  const canvas = Array.isArray(canvasArray) ? canvasArray[0] : canvasArray;
  if (!canvas || isEmpty.value) return;

  canvas.toBlob((blob) => {
    if (!blob) {
      showToast('No se pudo procesar el lienzo.');
      return;
    }

    const file = new File([blob], `firma_${Date.now()}.png`, { type: 'image/png' });

    engine.handleFileUpload(props.question.id, opt.id, file)
      .then((error) => {
        if (error) {
          showToast(`Error al enviar: ${error}`, 'error');
        }
      });
  }, 'image/png');
};

const removeSignature = (optionId) => {
  engine.setAnswer(props.question?.id, optionId, null);
};

</script>

<style scoped>
/* Canvas Container */
.pe-signature-wrapper {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
}

.pe-signature-canvas {
  width: 100%;
  height: 250px;
  border: var(--pe-drop-zone-border, 2px dashed #ccc);
  border-radius: var(--pe-drop-zone-border-radius, 0.5rem);
  background: var(--pe-drop-zone-bg, #fafafa);
  touch-action: none;
  cursor: crosshair;
  transition: border-color 0.2s;
}

.pe-signature-canvas:hover {
  border-color: var(--pe-primary-color, green);
}

.pe-signature-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Uploading State */
.pe-signature-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 250px;
  background: var(--pe-drop-zone-bg, #fafafa);
  border: var(--pe-drop-zone-border, 2px dashed #ccc);
  border-radius: var(--pe-drop-zone-border-radius, 0.5rem);
}

/* Inherited Styles */
.pe-ListOptions {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: var(--pe-qo-select-list-gap, 1.5vh);
  margin-top: 15px;
}

.pe-ListItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  background: var(--pe-selected-item-bg, #f5f5f5);
  border-radius: var(--pe-input-border-radius, 0.5rem);
  padding: var(--pe-qo-select-list-item-padding, 0.75vw);
  gap: var(--pe-options-gp, 1.5vh);
  width: fit-content;
}

.pe-file-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pe-signature-preview {
  max-width: 250px;
  max-height: 100px;
  object-fit: contain;
  border-radius: 0.5rem;
  background-color: white;
  /* Da contraste a png sin fondo */
  padding: 5px;
  border: 1px solid #ddd;
}

.pe-delete-btn {
  font-weight: bold;
  color: #666;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 5px;
}

.pe-delete-btn:hover {
  color: var(--pe-error-color, #ff4d4f);
}

/* Loading Spinner */
.spinner {
  width: var(--pe-loading-spinner-with, 1.5rem);
  aspect-ratio: 1/1;
  border: var(--pe-loading-spinner-border, 3px solid #eee);
  border-top-color: var(--pe-primary-color, green);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.uploading-text {
  color: #888;
  font-style: italic;
  margin-top: 10px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Errors */
.pe-error-container {
  display: flex;
  flex-direction: column;
  gap: var(--pe-toast-error-gap, 0.5rem);
  margin-top: 10px;
}

.pe-toast-error {
  padding: var(--pe-qo-select-error-item-padding, 0.75vw);
  border-radius: var(--pe-input-border-radius, 0.5rem);
}

.pe-toast-error.error {
  background: var(--pe-error-item-bg, rgba(211, 47, 47, 0.2));
  color: var(--pe-error-item-color, #333);
}
</style>
